import * as React from 'react';
import { graphql, ChildProps } from 'react-apollo';
import gql from 'graphql-tag';
import track, { TrackingProp } from 'react-tracking';
import { isEqual, get } from 'lodash';
import bind from '../../bind';

import SanitizedRender from '../../components/sanitized_render';

import CSPRow, { CSPCardFragment } from '../../components/csp_row';
import Tiles from '../../components/tiles';
import CspFeatureListItem from './csp_feature_list_item';
import { CSP_TYPE, VIEW, CTA, IEventData, trackCSPView } from '../../tracker';
import CoreLink from '../../components/core_link';
import * as I18n from 'i18n-js';
import { IDynamicComponentProps } from '../dynamic_component_props';
import {
  CommonsCmsCspCollection,
  reverb_search_CSPSearchRequest_Sort,
} from '../../rql';

export const FEATURE_TYPES = [
  'cspRow',
  'squareGrid',
  'orderedList',
];

export interface IProps extends IDynamicComponentProps {
  title?: string;
  titleHtml?: string;
  subtitleHtml?: string;
  ledeHtml?: string;
  collectionId?: string;
  query?: string;
  make?: string[];
  categoryId?: string;
  condition?: ConditionFilter;
  maxCount?: number;
  ctaText?: string;
  ctaTargetHref?: string;
  sort?: reverb_search_CSPSearchRequest_Sort;
  tracking?: TrackingProp;
  displayAsGrid?: boolean;
  jumplinkSlug?: string;
  useCspSummary?: boolean;

  // see FEATURE_TYPES
  displayAs?: string;
}

type ConditionFilter = 'new' | 'used';

const COMPONENT_NAME = 'CspCollection';

@track((props: IProps): IEventData => {
  return {
    componentName: COMPONENT_NAME,
    type: CSP_TYPE,
    context: {
      title: props.title,
      layoutSection: props.layoutSection,
      pagePosition: props.pagePosition,
      collectionId: props.collectionId,
      query: props.query,
      make: props.make,
      categoryId: props.categoryId,
      condition: props.condition,
      maxCount: props.maxCount,
      sort: props.sort,
    },
  };
})
export class CspCollection extends React.Component<ChildProps<IProps, CommonsCmsCspCollection.Query>, null> {
  static defaultProps: Partial<IProps> = {
    sort: reverb_search_CSPSearchRequest_Sort.TRENDING_DESC,
    maxCount: 12,
  };

  componentDidMount() {
    if (!this.props.data.loading && this.csps().length) {
      this.props.tracking.trackEvent({
        eventType: VIEW,
        ids: this.csps().map(csp => csp.id),
        mParticleView: trackCSPView(this.csps(), COMPONENT_NAME),
      });
    }
  }

  componentDidUpdate(oldProps) {
    if (!this.props.data.loading && this.csps().length && !isEqual(this.props.data, oldProps.data)) {
      this.props.tracking.trackEvent({
        eventType: VIEW,
        ids: this.csps().map(csp => csp.id),
        mParticleView: trackCSPView(this.csps(), COMPONENT_NAME),
      });
    }
  }

  action() {
    if (!this.props.ctaTargetHref) { return null; }
    return (
      <CoreLink
        interactionType={CTA}
        to={this.props.ctaTargetHref}
      >
        {this.props.ctaText ? this.props.ctaText : I18n.t('cms.comparisonShoppingPages.seeMore')}
      </CoreLink>
    );
  }

  csps(): CommonsCmsCspCollection.Csps[] {
    return get(this.props, 'data.cspSearch.csps', []);
  }

  displayAs(style) {
    return this.props.displayAs === style;
  }

  renderTitle() {
    return (
      <SanitizedRender
        html={this.props.titleHtml || this.props.title}
      />
    );
  }

  @bind
  renderOrderedListItem(csp, idx) {
    return (
      <CspFeatureListItem
        csp={csp}
        key={idx}
        listPosition={idx + 1}
        position={idx}
        useCspSummary={this.props.useCspSummary}
      />
    );
  }

  renderOrderedList() {
    return (
      <div className="csp-feature-list">
        <h2 className="csp-feature-list__heading">
          {this.renderTitle()}
        </h2>
        <Tiles
          oneWide
          loading={this.props.data.loading}
          placeholder={<CspFeatureListItem />}
        >
          {this.csps().map(this.renderOrderedListItem)}
        </Tiles>
      </div>
    );
  }

  render() {
    // legacy support for boolean flag
    const displayAsGrid = this.displayAs('squareGrid') || this.props.displayAsGrid;

    if (this.displayAs('orderedList')) {
      return this.renderOrderedList();
    }

    return (
      <CSPRow
        title={this.renderTitle()}
        subtitleHtml={this.props.subtitleHtml}
        ledeHtml={this.props.ledeHtml}
        id={this.props.jumplinkSlug}
        csps={this.csps()}
        expectedCount={this.props.maxCount}
        action={this.action()}
        loading={this.props.data.loading}
        conditionFilter={this.props.condition}
        displayAsGrid={displayAsGrid}
      />
    );
  }
}

const connect = graphql<IProps, CommonsCmsCspCollection.Query>(
  gql`
    query Commons_Cms_CspCollection(
      $curatedSetId: String
      $fullTextQuery: String
      $brandNames: [String]
      $condition: String
      $limit: Int
      $categoryUuids: [String]
      $sort: reverb_search_CSPSearchRequest_Sort
    )
    {
      cspSearch(input: {
        curatedSetId: $curatedSetId
        fullTextQuery: $fullTextQuery
        brandNames: $brandNames
        condition: $condition
        limit: $limit
        withInventory: true
        sort: $sort
        categoryUuids: $categoryUuids
      }) {
        csps {
          ...CSPCard
          metaDescription
          summary
          reviews {
            total
            avgRating
          }
          brand {
            name
          }
        }
      }
    }
    ${CSPCardFragment}
  `,
  {
    options: (ownProps) => {
      return {
        ssr: ownProps.ssr,
        variables: {
          curatedSetId: ownProps.collectionId,
          fullTextQuery: ownProps.query,
          brandNames: ownProps.make,
          condition: ownProps.condition,
          limit: ownProps.maxCount || 12,
          categoryUuids: ownProps.categoryId ? [ownProps.categoryId] : [],
          sort: ownProps.sort || reverb_search_CSPSearchRequest_Sort.TRENDING_DESC,
        },
      };
    },
  },
);

export default connect(CspCollection);



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/cms/components/csp_collection.tsx